import { Card, Text } from '@fluentui/react-components';
import {
  bundleIcon,
  ChevronDown16Filled,
  ChevronDown16Regular,
  ChevronRight16Filled,
  ChevronRight16Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';

import type { GroundingDataSource } from '../../../types/debug';

import type { GroundingDataSourcePanelProps } from './GroundingDataSourcePanel.interface';
import { useStyles } from './GroundingDataSourcePanel.style';

const ExpandIcon = bundleIcon(
  ChevronDown16Filled,
  ChevronDown16Regular,
) as React.FC;
const CollapseIcon = bundleIcon(
  ChevronRight16Filled,
  ChevronRight16Regular,
) as React.FC;

interface DataSourceCardProps {
  source: GroundingDataSource;
  index: number;
}

function DataSourceCard({ source, index }: DataSourceCardProps) {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(true);

  return (
    <Card className={styles.sourceCard}>
      <div
        className={styles.sourceHeader}
        onClick={() => setExpanded((e) => !e)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setExpanded((prev) => !prev);
          }
        }}
      >
        {expanded ? <ExpandIcon /> : <CollapseIcon />}
        <Text className={styles.sourceName}>
          {index + 1}. {source.sourceName || 'Unnamed Source'}
        </Text>
      </div>

      {expanded && (
        <>
          {source.executionStatement && (
            <>
              <Text className={styles.sectionLabel}>Execution Statement</Text>
              <div className={styles.execStatement}>
                {source.executionStatement}
              </div>
            </>
          )}

          {source.results.length > 0 && (
            <>
              <Text className={styles.sectionLabel}>
                Results ({source.results.length})
              </Text>
              <div className={styles.resultList}>
                {source.results.map((result) => (
                  <div key={result.Idx} className={styles.resultItem}>
                    <Text className={styles.resultIndex}>#{result.Idx}</Text>
                    {result.query && (
                      <>
                        <Text className={styles.resultFieldLabel}>Query</Text>
                        <Text className={styles.resultFieldValue}>
                          {result.query}
                        </Text>
                      </>
                    )}
                    {result.response && (
                      <>
                        <Text className={styles.resultFieldLabel}>
                          Response
                        </Text>
                        <Text className={styles.resultResponseValue}>
                          {result.response}
                        </Text>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </Card>
  );
}

export function GroundingDataSourcePanel({
  payload,
}: GroundingDataSourcePanelProps) {
  const styles = useStyles();

  return (
    <div className={styles.root}>
      {/* User intent + current thread */}
      <Card className={styles.metaCard}>
        <div className={styles.metaRow}>
          <Text className={styles.metaLabel}>User Intent</Text>
          {payload.userIntent ? (
            <Text className={styles.metaValue}>{payload.userIntent}</Text>
          ) : (
            <Text className={styles.metaEmpty}>(none)</Text>
          )}
        </div>
        {payload.currentThread && (
          <div className={styles.metaRow}>
            <Text className={styles.metaLabel}>Current Thread</Text>
            <Text className={styles.metaValue}>{payload.currentThread}</Text>
          </div>
        )}
      </Card>

      {/* Data sources */}
      {payload.dataSources.map((source, i) => (
        <DataSourceCard
          key={`${source.sourceName}-${i}`}
          source={source}
          index={i}
        />
      ))}
    </div>
  );
}
